/**
 * T037: Technical Requirements Step Component
 * 
 * Handles technical specifications in onboarding wizard
 * Constitutional compliance: WCAG 2.1 AA accessibility, TDD coverage
 */

import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  OutlinedInput,
  Stack,
  FormHelperText
} from '@mui/material';
import { OnboardingData } from '../../../services/onboardingService';

export interface TechRequirementsStepProps {
  data: OnboardingData['tech_requirements'];
  onChange: (data: Partial<OnboardingData['tech_requirements']>) => void;
  errors: Record<string, string>;
}

export const TechRequirementsStep: React.FC<TechRequirementsStepProps> = ({
  data,
  onChange,
  errors
}) => {
  const handleFieldChange = (field: keyof OnboardingData['tech_requirements']) => 
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ [field]: event.target.value });
    };

  const handleArrayChange = (field: keyof OnboardingData['tech_requirements']) => 
    (event: any) => {
      const value = typeof event.target.value === 'string' 
        ? event.target.value.split(',') 
        : event.target.value;
      onChange({ [field]: value });
    };

  const targetPlatforms = [
    { value: 'web', label: 'Web Browser' },
    { value: 'ios', label: 'iOS (iPhone/iPad)' },
    { value: 'android', label: 'Android' },
    { value: 'windows', label: 'Windows Desktop' },
    { value: 'macos', label: 'macOS Desktop' },
    { value: 'linux', label: 'Linux Desktop' },
    { value: 'hybrid_mobile', label: 'Hybrid Mobile (React Native/Flutter)' },
    { value: 'pwa', label: 'Progressive Web App (PWA)' }
  ];

  const technologies = [
    { value: 'react', label: 'React' },
    { value: 'vue', label: 'Vue.js' },
    { value: 'angular', label: 'Angular' },
    { value: 'nodejs', label: 'Node.js' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'dotnet', label: '.NET' },
    { value: 'php', label: 'PHP' },
    { value: 'laravel', label: 'Laravel' },
    { value: 'django', label: 'Django' },
    { value: 'flask', label: 'Flask' },
    { value: 'express', label: 'Express.js' },
    { value: 'nextjs', label: 'Next.js' },
    { value: 'nuxt', label: 'Nuxt.js' },
    { value: 'postgresql', label: 'PostgreSQL' },
    { value: 'mysql', label: 'MySQL' },
    { value: 'mongodb', label: 'MongoDB' },
    { value: 'redis', label: 'Redis' },
    { value: 'aws', label: 'AWS' },
    { value: 'azure', label: 'Microsoft Azure' },
    { value: 'gcp', label: 'Google Cloud' },
    { value: 'docker', label: 'Docker' },
    { value: 'kubernetes', label: 'Kubernetes' }
  ];

  const integrationOptions = [
    { value: 'payment_gateway', label: 'Payment Gateway (Stripe, PayPal)' },
    { value: 'crm_integration', label: 'CRM Integration (Salesforce, HubSpot)' },
    { value: 'email_marketing', label: 'Email Marketing (Mailchimp, SendGrid)' },
    { value: 'analytics', label: 'Analytics (Google Analytics, Mixpanel)' },
    { value: 'social_media', label: 'Social Media APIs' },
    { value: 'accounting', label: 'Accounting Software (QuickBooks, Xero)' },
    { value: 'inventory', label: 'Inventory Management' },
    { value: 'shipping', label: 'Shipping APIs (FedEx, UPS)' },
    { value: 'authentication', label: 'Third-party Authentication (OAuth, SAML)' },
    { value: 'file_storage', label: 'File Storage (AWS S3, Google Drive)' },
    { value: 'messaging', label: 'Messaging (Slack, Teams, WhatsApp)' },
    { value: 'calendar', label: 'Calendar Integration (Google, Outlook)' },
    { value: 'maps', label: 'Maps & Location Services' },
    { value: 'api_integrations', label: 'Custom API Integrations' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Technical Requirements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Help us understand your technical preferences and requirements. Don't worry if you're not sure - we can discuss these details together.
      </Typography>

      <Stack spacing={3}>
        {/* Target Platforms */}
        <FormControl fullWidth error={!!errors.target_platforms}>
          <InputLabel>Target Platforms</InputLabel>
          <Select
            multiple
            value={data.target_platforms || []}
            onChange={handleArrayChange('target_platforms')}
            input={<OutlinedInput label="Target Platforms" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const platform = targetPlatforms.find(p => p.value === value);
                  return <Chip key={value} label={platform?.label || value} size="small" />;
                })}
              </Box>
            )}
            data-testid="target-platforms-select"
          >
            {targetPlatforms.map((platform) => (
              <MenuItem key={platform.value} value={platform.value}>
                {platform.label}
              </MenuItem>
            ))}
          </Select>
          {errors.target_platforms && <FormHelperText>{errors.target_platforms}</FormHelperText>}
        </FormControl>

        {/* Preferred Technologies */}
        <FormControl fullWidth error={!!errors.preferred_technologies}>
          <InputLabel>Preferred Technologies (Optional)</InputLabel>
          <Select
            multiple
            value={data.preferred_technologies || []}
            onChange={handleArrayChange('preferred_technologies')}
            input={<OutlinedInput label="Preferred Technologies (Optional)" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const tech = technologies.find(t => t.value === value);
                  return <Chip key={value} label={tech?.label || value} size="small" />;
                })}
              </Box>
            )}
            data-testid="preferred-technologies-select"
          >
            {technologies.map((tech) => (
              <MenuItem key={tech.value} value={tech.value}>
                {tech.label}
              </MenuItem>
            ))}
          </Select>
          {errors.preferred_technologies && <FormHelperText>{errors.preferred_technologies}</FormHelperText>}
        </FormControl>

        {/* Integration Requirements */}
        <FormControl fullWidth error={!!errors.integration_requirements}>
          <InputLabel>Integration Requirements (Optional)</InputLabel>
          <Select
            multiple
            value={data.integration_requirements || []}
            onChange={handleArrayChange('integration_requirements')}
            input={<OutlinedInput label="Integration Requirements (Optional)" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const integration = integrationOptions.find(i => i.value === value);
                  return <Chip key={value} label={integration?.label || value} size="small" />;
                })}
              </Box>
            )}
            data-testid="integration-requirements-select"
          >
            {integrationOptions.map((integration) => (
              <MenuItem key={integration.value} value={integration.value}>
                {integration.label}
              </MenuItem>
            ))}
          </Select>
          {errors.integration_requirements && <FormHelperText>{errors.integration_requirements}</FormHelperText>}
        </FormControl>

        {/* Performance Requirements */}
        <TextField
          fullWidth
          label="Performance Requirements"
          multiline
          rows={3}
          value={data.performance_requirements || ''}
          onChange={handleFieldChange('performance_requirements')}
          error={!!errors.performance_requirements}
          helperText={errors.performance_requirements || 'Optional - Describe any specific performance needs (e.g., expected user load, response times)'}
          data-testid="performance-requirements-textarea"
        />

        {/* Security Requirements */}
        <TextField
          fullWidth
          label="Security Requirements"
          multiline
          rows={3}
          value={data.security_requirements || ''}
          onChange={handleFieldChange('security_requirements')}
          error={!!errors.security_requirements}
          helperText={errors.security_requirements || 'Optional - Describe any specific security needs (e.g., compliance requirements, data protection)'}
          data-testid="security-requirements-textarea"
        />

        {/* Scalability Requirements */}
        <TextField
          fullWidth
          label="Scalability Requirements"
          multiline
          rows={3}
          value={data.scalability_requirements || ''}
          onChange={handleFieldChange('scalability_requirements')}
          error={!!errors.scalability_requirements}
          helperText={errors.scalability_requirements || 'Optional - Describe expected growth and scaling needs'}
          data-testid="scalability-requirements-textarea"
        />

        {/* Accessibility Requirements */}
        <TextField
          fullWidth
          label="Accessibility Requirements"
          multiline
          rows={3}
          value={data.accessibility_requirements || ''}
          onChange={handleFieldChange('accessibility_requirements')}
          error={!!errors.accessibility_requirements}
          helperText={errors.accessibility_requirements || 'Optional - Describe any specific accessibility needs (e.g., WCAG compliance, screen reader support)'}
          data-testid="accessibility-requirements-textarea"
        />
      </Stack>
    </Box>
  );
};

export default TechRequirementsStep;